import { Component, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { FullCalendarComponent } from "@fullcalendar/angular";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { CalendarOptions, DatesSetArg, EventClickArg, EventInput } from "@fullcalendar/core";
import { Subscription } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { VisitFormComponent, VisitFormData } from "./visit-form/visit-form.component";
import { VisitDetailComponent, VisitDetailResult } from "./visit-detail/visit-detail.component";
import { apiErrorMessage } from "./visit-time";
import { ClickToCallComponent } from "../calendar-events/click-to-call/click-to-call.component";

/** The fields of an `api/visits/list` row the planner uses. */
interface PlannerVisit {
  visitKey: number;
  title: string;
  visitType: "VIDEO" | "IN_PERSON" | string;
  status?: string;
  startUtc: string;
  endUtc: string;
  location?: string | null;
}

type PlannerView = "timeGridDay" | "timeGridWeek" | "dayGridMonth" | "listWeek";

const VIEWS: { type: PlannerView; label: string }[] = [
  { type: "timeGridDay", label: "Day" },
  { type: "timeGridWeek", label: "Week" },
  { type: "dayGridMonth", label: "Month" },
  { type: "listWeek", label: "List" },
];

/** Below this width the planner opens on the list, which reads better than a 7-column grid. */
const NARROW_PX = 720;

/**
 * Visit Planner: a week of in-person and video visits on the live `api/visits/*` API.
 * FullCalendar draws the grid; the toolbar, day headers, visit cards and empty state are this
 * component's own templates. Click a slot for a new visit, a visit for its details.
 */
@Component({
  selector: "app-visit-planner",
  templateUrl: "./visit-planner.component.html",
  styleUrls: ["./visit-planner.component.scss"],
  standalone: false,
})
export class VisitPlannerComponent implements OnDestroy {
  @ViewChild(FullCalendarComponent) calendar?: FullCalendarComponent;

  readonly views = VIEWS;
  calendarOptions: CalendarOptions;
  view: PlannerView;
  title = "";
  todayInRange = true;
  loading = false;
  errorMessage = "";
  showCancelled = false;

  private visits: PlannerVisit[] = [];
  private range: { start: Date; end: Date } | null = null;
  private loadSub?: Subscription;
  private querySub?: Subscription;
  private createOpened = false;

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.view = typeof window !== "undefined" && window.innerWidth < NARROW_PX ? "listWeek" : "timeGridWeek";
    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const create = (params.get("create") || "").toLowerCase();
      if (create !== "1" && create !== "meeting") return;
      if (this.createOpened) return;
      this.createOpened = true;
      const type = (params.get("type") || "VIDEO").toUpperCase() === "IN_PERSON" ? "IN_PERSON" : "VIDEO";
      const title = params.get("title") || "";
      void Promise.resolve().then(() => {
        this.openForm({ visitType: type, title });
        this.clearCreateQuery();
      });
    });
    this.calendarOptions = {
      initialView: this.view,
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      headerToolbar: false,
      height: "100%",
      nowIndicator: true,
      allDaySlot: false,
      scrollTime: "08:00:00",
      slotDuration: "00:30:00",
      slotLabelFormat: { hour: "numeric", meridiem: "short" },
      eventTimeFormat: { hour: "numeric", minute: "2-digit", meridiem: "short" },
      businessHours: { daysOfWeek: [1, 2, 3, 4, 5], startTime: "08:00", endTime: "18:00" },
      eventDisplay: "block",
      dayMaxEvents: 3,
      views: {
        listWeek: { listDayFormat: { weekday: "long", month: "short", day: "numeric" }, listDaySideFormat: false },
      },
      events: [],
      // FullCalendar fires datesSet while it renders, inside this component's change detection;
      // flipping `loading` there throws NG0100, so the reload starts just after that pass.
      datesSet: (arg: DatesSetArg) => void Promise.resolve().then(() => this.onDatesSet(arg)),
      dateClick: (arg: DateClickArg) => this.openForm({ start: arg.date }),
      eventClick: (arg: EventClickArg) => this.openDetail(Number(arg.event.id)),
    };
  }

  // ── Counts for the legend and the empty state ──────────────────────────

  get inPersonCount(): number {
    return this.visits.filter((v) => !isCancelled(v) && !isVideo(v)).length;
  }

  get videoCount(): number {
    return this.visits.filter((v) => !isCancelled(v) && isVideo(v)).length;
  }

  get cancelledCount(): number {
    return this.visits.filter(isCancelled).length;
  }

  get emptyRange(): boolean {
    return !this.loading && !this.errorMessage && this.inPersonCount + this.videoCount === 0;
  }

  get rangeNoun(): string {
    if (this.view === "timeGridDay") return "day";
    if (this.view === "dayGridMonth") return "month";
    return "week";
  }

  // ── Toolbar ─────────────────────────────────────────────────────────────

  prev(): void {
    this.calendar?.getApi().prev();
  }

  next(): void {
    this.calendar?.getApi().next();
  }

  today(): void {
    this.calendar?.getApi().today();
  }

  changeView(view: PlannerView): void {
    if (view !== this.view) this.calendar?.getApi().changeView(view);
  }

  newVisit(): void {
    this.openForm({});
  }

  /** The same call panel as the Events page's "Phone call" button (EventScheduleComponent). */
  openPhoneCall(): void {
    ClickToCallComponent.open(this.dialog);
  }

  toggleCancelled(): void {
    this.showCancelled = !this.showCancelled;
    this.render();
  }

  refresh(): void {
    if (!this.range) return;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.errorMessage = "";
    this.loadSub = this.apiService
      .listVisits({ fromUtc: this.range.start.toISOString(), toUtc: this.range.end.toISOString() })
      .subscribe({
        next: (res) => {
          this.visits = Array.isArray(res) ? (res as PlannerVisit[]) : [];
          this.loading = false;
          this.render();
        },
        error: (err) => {
          this.visits = [];
          this.loading = false;
          this.errorMessage = apiErrorMessage(err, "Visits could not be loaded.");
          this.render();
        },
      });
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.querySub?.unsubscribe();
  }

  /** Drop create/type/title after opening the form so a refresh does not reopen it. */
  private clearCreateQuery(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { create: null, type: null, title: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private onDatesSet(arg: DatesSetArg): void {
    this.title = arg.view.title;
    this.view = arg.view.type as PlannerView;
    const now = Date.now();
    this.todayInRange = arg.view.currentStart.getTime() <= now && now < arg.view.currentEnd.getTime();
    const same = this.range && this.range.start.getTime() === arg.start.getTime() && this.range.end.getTime() === arg.end.getTime();
    this.range = { start: arg.start, end: arg.end };
    if (!same) this.refresh();
  }

  private render(): void {
    const events: EventInput[] = this.visits
      .filter((v) => this.showCancelled || !isCancelled(v))
      .map((v) => {
        const cancelled = isCancelled(v);
        const video = isVideo(v);
        return {
          id: String(v.visitKey),
          title: v.title,
          start: v.startUtc,
          end: v.endUtc,
          classNames: ["visit-event", video ? "visit-video" : "visit-in-person", ...(cancelled ? ["visit-cancelled"] : [])],
          extendedProps: { video, cancelled, location: v.location || "" },
        };
      });
    this.calendarOptions = { ...this.calendarOptions, events };
  }

  /** Visits of 30 minutes or less get a one-line card: two lines do not fit a 30-minute slot. */
  isShort(start: Date | null, end: Date | null): boolean {
    return !!start && !!end && end.getTime() - start.getTime() <= 30 * 60000;
  }

  private openForm(data: VisitFormData): void {
    this.dialog
      .open(VisitFormComponent, {
        width: "760px",
        maxWidth: "calc(100vw - 24px)",
        maxHeight: "calc(100dvh - 32px)",
        panelClass: "vf-dialog",
        autoFocus: "#vf-title",
        data,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.refresh();
      });
  }

  private openDetail(visitKey: number): void {
    this.dialog
      .open(VisitDetailComponent, {
        width: "620px",
        maxWidth: "calc(100vw - 24px)",
        maxHeight: "calc(100dvh - 32px)",
        panelClass: "vd-dialog",
        autoFocus: "dialog",
        data: { visitKey },
      })
      .afterClosed()
      .subscribe((result: VisitDetailResult | undefined) => {
        if (result?.action === "edit") this.openForm({ visit: result.visit });
        else if (result?.action === "changed") this.refresh();
      });
  }
}

function isCancelled(v: PlannerVisit): boolean {
  return v.status === "cancelled";
}

function isVideo(v: PlannerVisit): boolean {
  return v.visitType === "VIDEO";
}
